import Link from 'next/link';
import { useState } from 'react';

import { Book, Order, UserProfile } from 'types';

import ShareSVG from '~/assets/svg/icons/share';

import Links from '~/lib/helpers/link-helper';
import useAuthenticatedAction from '~/lib/hooks/use-authenticated-action';
import useMessages from '~/lib/hooks/use-messages';

import { useGlobalState } from '~/state';
import { ActionType } from '~/state/types';

import Button from '../button';
import SessionForm from '../session-form';

import styles from '~/styles/pages/messages.module.scss';
import MessageSVG from '~/assets/svg/icons/message';
import COLORS from '~/lib/helpers/color-helper';

interface MessageComponentProps {
  book?: Book;
  order?: Order;
  hideSendInMessenger?: boolean;
  user: UserProfile;
  sellerToBuyer?: boolean;
}
export const MessageComponent = (props: MessageComponentProps) => {
  const [message, setMessage] = useState<string>('');
  const { sendMessage } = useMessages();
  const { dispatch } = useGlobalState();
  const [loading, setLoading] = useState<boolean>(false);

  const book = props.book || props.order?.book_data;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = message;
    setLoading(true);
    await sendMessage({
      text,
      sellerToBuyer: props.sellerToBuyer,
      ...(props.book && { book: props.book }),
      ...(props.order && { order: props.order }),
    })
      .then((res) => {
        dispatch({
          type: ActionType.ADD_MODAL_DATA,
          payload: {
            title: 'Success',
            message: `${props.user.username} has received your messge about ${book.title} by ${book.author}.`,
            component: null,
            showDismiss: true,
            dismissText: 'Okay',
          },
        });
      })
      .catch((e) => {
        dispatch({
          type: ActionType.ADD_MODAL_DATA,
          payload: {
            title: 'Something went wrong',
            message: `Please try again or contact support for assistance`,
            component: null,
            showDismiss: true,
            dismissText: 'Okay',
          },
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className={styles['msg-component-container']}>
      <form onSubmit={handleSubmit}>
        <div className={styles['msg-input']}>
          <textarea
            className={styles['input-field']}
            placeholder='Send a message'
            onChange={(e) => setMessage(e.target.value)}
            value={message}
            required
          />
          <div className={styles['send-btn']}>
            <Button
              text='Send'
              size='small'
              type='submit'
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
      </form>
      {!props.hideSendInMessenger && (
        <Link
          href={Links.messages.general({
            type: 'userId',
            id: book.seller_id,
            bookId: book.id,
          })}
          target='_blank'
        >
          <div className={styles['see-messenger-container']}>
            <p>
              or{' '}
              <span className={styles['see-messenger-text']}>
                send in your messenger
              </span>
            </p>
            <div className={styles['see-messenger-icon']}>
              <ShareSVG />
            </div>
          </div>
        </Link>
      )}
    </div>
  );
};

interface MessageButtonProps {
  book: Book;
  user: UserProfile;
  style: 'small' | 'large';
}

export default function MessageButton({ style = 'large', ...props }: MessageButtonProps) {
  const { dispatch } = useGlobalState();

  const { handleAuthAction } = useAuthenticatedAction({
    actionToCall: () => {
      if (props.user) {
        dispatch({
          type: ActionType.ADD_MODAL_DATA,
          payload: {
            title: `Send a message to ${props.user?.name}`,
            message: `${props.book.title} by ${props.book.author}`,
            component: <MessageComponent book={props.book} user={props.user} />,
          },
        });
      }
    },
  });

  async function handleMessage(e) {
    e.preventDefault();

    await handleAuthAction({
      title: 'Login to Message Seller',
      message:
        "You need an account to message this seller, please sign in with one of the methods below.",
      component: <SessionForm />,
    });
  }

  return (
    <Button
      className={styles['seller-message-button']}
      loading={!props.user}
      text={props.user && style !== 'small' && 'Message seller'}
      style='outline'
      size='small'
      onPress={handleMessage}
      icon={
        style === 'small' && <MessageSVG fillColor={COLORS.primary} />
      }
    />
  );
}
